import { Check, MessageSquare, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { formatDateTime } from '../../../../utils/dateFormat';
import type { AdminConversation } from '../../../../services/adminAgent.service';
import { useRenameConversation } from '../hooks/useAdminConversations';

const DEFAULT_TITLE = 'Conversation';
/** Matches the server cap for conversation titles. */
const TITLE_MAX_LENGTH = 80;

interface ConversationRowProps {
  conversation: AdminConversation;
  isCurrent: boolean;
  onSelect: (conversationPublicId: string) => void;
}

/** One entry of the conversation list: open it, or rename it in place. */
export const ConversationRow: React.FC<ConversationRowProps> = ({
  conversation,
  isCurrent,
  onSelect,
}) => {
  const rename = useRenameConversation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEditing = () => {
    setDraft(conversation.title ?? '');
    setEditing(true);
  };
  const save = () => {
    const title = draft.trim();
    if (!title || title === conversation.title) return setEditing(false);
    rename.mutate(
      { conversationPublicId: conversation.conversationPublicId, title },
      { onSuccess: () => setEditing(false) }
    );
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-4 py-2">
        <input
          autoFocus
          value={draft}
          maxLength={TITLE_MAX_LENGTH}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') save();
            if (event.key === 'Escape') setEditing(false);
          }}
          aria-label="Conversation title"
          className="min-w-0 flex-1 rounded-md border border-slate-300 bg-transparent px-2 py-1 text-sm text-slate-900 outline-none focus:border-blue-500 dark:border-slate-600 dark:text-slate-100"
        />
        <button
          type="button"
          onClick={save}
          disabled={rename.isPending}
          aria-label="Save title"
          className="rounded-md p-1.5 text-green-600 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-700"
        >
          <Check size={16} />
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          aria-label="Cancel rename"
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group flex items-start gap-1 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
        isCurrent ? 'bg-blue-50 dark:bg-slate-700/60' : ''
      }`}
    >
      <button
        type="button"
        onClick={() => onSelect(conversation.conversationPublicId)}
        className="flex min-w-0 flex-1 items-start gap-2 px-4 py-3 text-left"
      >
        <MessageSquare size={16} className="mt-0.5 shrink-0 text-slate-400" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {conversation.title || DEFAULT_TITLE}
          </span>
          <span className="block text-xs text-slate-500 dark:text-slate-400">
            {formatDateTime(conversation.lastMessageAt ?? conversation.createdAt)}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={startEditing}
        title="Rename"
        aria-label="Rename conversation"
        className="mr-3 mt-2.5 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
      >
        <Pencil size={14} />
      </button>
    </div>
  );
};
